// src/components/StockManagement/StockManagementTable.jsx
import React from 'react';

const StockManagementTable = ({ stockManagements, allFilteredData, isLoading, viewType }) => {
  console.log('StockManagementTable props:', { 
    stockManagements, 
    isLoading, 
    viewType,
    stockManagementCount: stockManagements?.length 
  });
  
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '-';
    return `Rs.${parseFloat(value).toFixed(2)}`;
  };

  const getInventoryName = (inventory) => {
    return inventory?.name || 'Unknown';
  };

  const getCategoryName = (category) => {
    return category?.name || 'Unknown';
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    } catch (error) {
      return '-';
    }
  };

  const calculateTotalCost = (item) => {
    const qty = item.qty || 0;
    const unitCost = item.unitCost || item.stock?.unitCost || 0;
    return qty * unitCost;
  };
  // Grand total calculated from ALL filtered data (not just current page)
const calculateGrandTotal = () => {
  const data = allFilteredData || stockManagements || [];
  if (data.length === 0) return 0;
  return data.reduce((sum, item) => sum + calculateTotalCost(item), 0);
};

const grandTotal = calculateGrandTotal();
const totalItemCount = (allFilteredData || stockManagements)?.length || 0;

  // ========================================
  // EDIT THESE HEADERS AS NEEDED
  // ========================================
  
  // Headers for CURRENT STOCK view
  const CURRENT_STOCK_HEADERS = [
    'ID',
    'Inventory',
    'Category',
    'Product Name',
    'Qty',
    'Recently Updated',
    'Unit Cost',
    'Price per unit',
    'Total Cost'
  ];

  // Headers for MOVEMENT LOG view
  const MOVEMENT_LOG_HEADERS = [
    'ID',
    'Type',
    'From Inventory',
    'To Inventory',
    'Category',
    'Product Name',
    'Qty',
    'Date',
    'Unit Cost',
    'Price per unit',
    'Total Cost'
  ];

  // ========================================
  // EDIT THESE TABLE BODIES AS NEEDED
  // ========================================
  
  // Render row for CURRENT STOCK view
  const renderCurrentStockRow = (item) => {
    return (
      <tr key={item.id || item.uniqueId}>
        <td>{item.id}</td>
        <td>{getInventoryName(item.inventory)}</td>
        <td>{getCategoryName(item.category)}</td>
        <td>{item.productName}</td>
        <td className={item.qty < 100 ? 'low-stock-qty' : ''}>
          {item.qty}
        </td>
        <td>{formatDate(item.createdAt)}</td>
        <td>{formatCurrency(item.unitCost)}</td>
        <td>{formatCurrency(item.pricePerUnit)}</td>
        <td>{formatCurrency(calculateTotalCost(item))}</td>
      </tr>
    );
  };

  // Render row for MOVEMENT LOG view
  const renderMovementLogRow = (item) => {
    return (
      <tr key={item.uniqueId || `${item.type}_${item.id}`}>
        <td>{item.id}</td>
        <td className={item.type === 'IN' ? 'type-in' : item.type === 'OUT' ? 'type-out' : 'type-transfer'}>
          {item.type}
        </td>
        <td>{item.fromInventory?.name || '-'}</td>
        <td>{item.toInventory?.name || '-'}</td>
        <td>{getCategoryName(item.category || item.stock?.category)}</td>
        <td>{item.productName || item.stock?.productName}</td>
        <td>{item.qty}</td>
        <td>{formatDate(item.createdAt)}</td>
        <td>{formatCurrency(item.unitCost || item.stock?.unitCost)}</td>
        <td>{formatCurrency(item.pricePerUnit || item.stock?.pricePerUnit)}</td>
        <td>{formatCurrency(calculateTotalCost(item))}</td>
      </tr>
    );
  };

  // ========================================
  
  const getTableHeaders = () => {
    if (viewType === 'CURRENT') {
      return CURRENT_STOCK_HEADERS;
    }
    return MOVEMENT_LOG_HEADERS;
  };

  const renderTableRow = (item) => {
    if (viewType === 'CURRENT') {
      return renderCurrentStockRow(item);
    }
    return renderMovementLogRow(item);
  };

  const tableHeaders = getTableHeaders();

  return (
   <>
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
    {/* Total Cost Widget (Current Stock only) */}
    {viewType === 'CURRENT' && (
      <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 20px',
      margin: '0 0 12px 0',
      width: 'fit-content',
      background: 'linear-gradient(135deg, #004080 0%, #2c417c 100%)',
      borderRadius: '10px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
      border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{
          width: '42px', height: '42px', borderRadius: '10px',
          background: 'rgba(99, 202, 183, 0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#63cab7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
        </div>
        <div>
          <div style={{
            fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: '2px',
          }}>
            Total Stock Value (All Filtered Results)
          </div>
          <div style={{
            fontSize: '22px', fontWeight: '700', color: '#63cab7',
            letterSpacing: '-0.02em', lineHeight: 1,
          }}>
            Rs.{grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: '20px',
            background: 'rgba(99, 202, 183, 0.12)', border: '1px solid rgba(99, 202, 183, 0.25)',
            fontSize: '12px', fontWeight: '600', color: '#63cab7',
          }}>
            {totalItemCount} items
          </span>
        </div>
      </div>
      
    )}
    </div>
 <div className="data-table-container"> 
      <table className="data-table">
        <thead  id="stockLogTableHead">
          <tr>
            {tableHeaders.map((header, idx) => (
              <th key={idx}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody id="stockLogTableBody">
          {isLoading ? (
            <tr>
              <td colSpan={tableHeaders.length} style={{ textAlign: 'center', padding: '20px' }}>
                Loading...
              </td>
            </tr>
          ) : stockManagements.length === 0 ? (
            <tr>
              <td colSpan={tableHeaders.length} style={{ textAlign: 'center', padding: '20px' }}>
                No data found
              </td>
            </tr>
          ) : (
            stockManagements.map(item => renderTableRow(item))
          )}
        </tbody>
      </table>
    </div>
    </>
  );
};

export default StockManagementTable;
